import { api } from '@/lib/api'
import { AccountProfile } from '@/lib/types'
import type { NotificationCatalogEntry, PortRequestStatus, RecentActivitiesResponse } from '@tenfour/contracts'

function mapAccount(raw: any): any {
  // Pass through all raw API fields directly — no transformation or field stripping.
  // Individual pages read what they need.
  return {
    ...raw,
    hourlyRate: raw.hourlyRate != null ? Number(raw.hourlyRate) : 0,
    minimumCharge: raw.minimumCharge != null ? Number(raw.minimumCharge) : undefined,
    taxRate: raw.taxRate != null ? Number(raw.taxRate) : undefined,
    enabledPaymentMethods: raw.enabledPaymentMethods ?? [],
  }
}

export async function getAccountProfile(accountId: string): Promise<AccountProfile> {
  const raw = await api<any>(`/accounts/${accountId}`)
  return mapAccount(raw)
}

/** Pulse intake timeline (last few activities) + the LLM-generated recap. */
export async function getRecentActivities(accountId: string): Promise<RecentActivitiesResponse> {
  return api<RecentActivitiesResponse>(`/accounts/${accountId}/recent-activities`)
}

/** Upload the account picture through the capability-guarded Accounts API. */
export async function uploadAccountLogo(
  accountId: string,
  data: string,
  contentType: string,
): Promise<{ logoUrl: string }> {
  return api<{ logoUrl: string }>(`/accounts/${accountId}/logo`, {
    method: 'POST',
    body: JSON.stringify({ data, contentType }),
  })
}

export async function updateAccountProfile(
  accountId: string,
  updates: Record<string, unknown>,
): Promise<void> {
  // Strip undefined values so we only send fields that were explicitly set
  const body: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(updates)) {
    if (value !== undefined) body[key] = value
  }
  await api(`/accounts/${accountId}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  })
}

export async function finishSetup(accountId: string): Promise<{ onboardingStatus: 'TWILIO_PENDING' | 'COMPLETE' }> {
  return api(`/accounts/${accountId}/finish-setup`, { method: 'POST' })
}

export type PortRequestInput = {
  phoneNumber: string
  customerType: 'individual' | 'business'
  accountName: string
  currentCarrier?: string
  accountNumber: string
  accountPin?: string
  billingStreet: string
  billingCity: string
  billingState: string
  billingZip: string
  authorizedEmail: string
}

// Request to port an existing business number into Ten Four (setup wizard).
// Returns a non-sensitive confirmation (id, phoneNumber, status).
export async function createPortRequest(
  accountId: string,
  input: PortRequestInput,
): Promise<{ id: string; phoneNumber: string; status: string; createdAt: string }> {
  return api(`/accounts/${accountId}/port-request`, {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

// The account's own transfer status, for the Settings row.
// `null` when they never ported — most take a new Ten Four number, and the UI
// shows nothing rather than an empty state. Carrier credentials are never in
// this response; the API selects only the contractor-safe fields.
export async function getPortRequest(accountId: string): Promise<PortRequestStatus | null> {
  return api(`/accounts/${accountId}/port-request`)
}
/**
 * Bumped whenever the matching legal doc is materially updated. The account
 * re-accepts each version, and the ledger row records exactly which versions
 * they agreed to.
 *
 * Lived inside the /setup wizard until deleted that page. Moved here, next
 * to the call that sends it, so the versions and the request cannot drift and
 * the next caller does not redefine them.
 */
export const LEGAL_VERSIONS = {
  tos: '2026-05-06',
  privacy: '2026-05-06',
  aup: '2026-05-06',
  dpa: '2026-05-05',
} as const

export async function recordConsent(
  accountId: string,
  data: {
    tosVersion: string
    privacyVersion: string
    aupVersion: string
    dpaVersion: string
    smsConsentAccepted: boolean
    marketingAccept: boolean
  },
): Promise<void> {
  await api(`/accounts/${accountId}/consent`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

// ── Voice settings (TTS voice per voice persona) ───────────────────────────

export interface VoiceLibraryModel {
  name: string
  label: string | null
  provider: string
  modelPattern: string
  voices: string[]
  /** True when picking from this model needs Pro+ (subscription tiers). */
  tierLocked: boolean
}

export interface VoicePersonaSettings {
  type: 'lyra' | 'aria'
  displayName: string
  templateName: string
  voiceModel: { name: string; label: string | null; provider: string; modelPattern: string } | null
  voice: string | null
  voices: string[]
  /** Whole enabled catalog — the Pro+ voice library picker. */
  availableModels: VoiceLibraryModel[]
}

export async function getVoiceSettings(
  accountId: string,
): Promise<{ personas: VoicePersonaSettings[]; allVoicesUnlocked: boolean }> {
  return api(`/accounts/${accountId}/voice-settings`)
}

export async function updateVoiceSetting(
  accountId: string,
  personaType: 'lyra' | 'aria',
  voice: string,
  voiceModel?: string,
): Promise<{ success: boolean; persona: VoicePersonaSettings }> {
  return api(`/accounts/${accountId}/voice-settings`, {
    method: 'PATCH',
    body: JSON.stringify(voiceModel ? { personaType, voice, voiceModel } : { personaType, voice }),
  })
}

// ── Call handling (plans/call-handoff-and-trust.md) ─────────────────────────

export type CallTrustLevel = 'AI_LED' | 'BALANCED' | 'HUMAN_FIRST'

/** {version:1, days:{mon:[[startMin,endMin],…],…}} — see @tenfour/contracts. */
export interface ConciergeSchedule {
  version: 1
  days: Partial<Record<'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun', [number, number][]>>
}

export interface CallHandlingSettings {
  callTrustLevel: CallTrustLevel
  /** null = the owner (default). */
  escalationTargetUserId: string | null
  /** Resolved number a handoff would dial right now (read-only; null = none). */
  escalationTargetPhone: string | null
  concierge: {
    enabled: boolean
    schedule: ConciergeSchedule
    backupUserId: string | null
    /** False when the tier config disables it — the card renders locked (TierGate). */
    tierUnlocked: boolean
  }
}

export interface UpdateCallHandlingPayload {
  callTrustLevel?: CallTrustLevel
  escalationTargetUserId?: string | null
  concierge?: {
    enabled?: boolean
    schedule?: ConciergeSchedule
    backupUserId?: string | null
  }
}

export async function getCallHandling(accountId: string): Promise<CallHandlingSettings> {
  return api(`/accounts/${accountId}/call-handling`)
}

export async function updateCallHandling(
  accountId: string,
  updates: UpdateCallHandlingPayload,
): Promise<CallHandlingSettings> {
  return api(`/accounts/${accountId}/call-handling`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  })
}

// ── Concierge growth settings (birthday texts) ─────────────────────────────

export interface BirthdaySettings {
  enabled: boolean
  template: string
  sendHourLocal: number
  /** False when the tier config disables it — the page renders locked (TierGate). */
  tierUnlocked: boolean
}

export async function getBirthdaySettings(accountId: string): Promise<BirthdaySettings> {
  return api(`/accounts/${accountId}/birthday-settings`)
}

export async function updateBirthdaySettings(
  accountId: string,
  updates: Partial<Pick<BirthdaySettings, 'enabled' | 'template' | 'sendHourLocal'>>,
): Promise<BirthdaySettings> {
  return api(`/accounts/${accountId}/birthday-settings`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  })
}

// ── Concierge growth settings (review follow-ups) ──────────────────────────

export interface ReviewSettings {
  enabled: boolean
  googleReviewUrl: string | null
  facebookReviewUrl: string | null
  yelpReviewUrl: string | null
  delayDays: number
  template: string
  /** Tracked-link click counts per platform (0 = no clicks / never emitted). */
  clicks: { google: number; facebook: number; yelp: number }
  tierUnlocked: boolean
}

export async function getReviewSettings(accountId: string): Promise<ReviewSettings> {
  return api(`/accounts/${accountId}/review-settings`)
}

export async function updateReviewSettings(
  accountId: string,
  updates: Partial<Omit<ReviewSettings, 'tierUnlocked' | 'clicks'>>,
): Promise<ReviewSettings> {
  return api(`/accounts/${accountId}/review-settings`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  })
}

// ── Concierge growth settings (customer subscription plans) ────────────────

export interface SubscriptionPlanRow {
  id: string
  name: string
  description: string | null
  priceCents: number
  interval: 'MONTH' | 'YEAR'
  discountPercent: number
  freeVisitsPerPeriod: number
  active: boolean
  activeSubscribers: number
  createdAt: string
}

export interface CustomerSubscriptionRow {
  id: string
  status: string
  currentPeriodEnd: string | null
  canceledAt: string | null
  createdAt: string
  plan: { id: string; name: string; priceCents: number; interval: 'MONTH' | 'YEAR' }
  customer: { id: string; name: string; phone: string }
}

export async function listSubscriptionPlans(
  accountId: string,
): Promise<{ tierUnlocked: boolean; plans: SubscriptionPlanRow[] }> {
  return api(`/accounts/${accountId}/subscription-plans`)
}

export async function createSubscriptionPlan(
  accountId: string,
  plan: {
    name: string
    description?: string
    priceCents: number
    interval?: 'MONTH' | 'YEAR'
    discountPercent?: number
    freeVisitsPerPeriod?: number
  },
): Promise<{ id: string }> {
  return api(`/accounts/${accountId}/subscription-plans`, {
    method: 'POST',
    body: JSON.stringify(plan),
  })
}

export async function updateSubscriptionPlan(
  accountId: string,
  planId: string,
  updates: Partial<{
    name: string
    description: string
    discountPercent: number
    freeVisitsPerPeriod: number
    active: boolean
  }>,
): Promise<{ success: boolean }> {
  return api(`/accounts/${accountId}/subscription-plans/${planId}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  })
}

export async function deleteSubscriptionPlan(
  accountId: string,
  planId: string,
): Promise<{ deleted: boolean }> {
  return api(`/accounts/${accountId}/subscription-plans/${planId}`, { method: 'DELETE' })
}

export async function listCustomerSubscriptions(
  accountId: string,
): Promise<{ subscriptions: CustomerSubscriptionRow[] }> {
  return api(`/accounts/${accountId}/customer-subscriptions`)
}

// ── Service catalog ────────────────────────────────────────────────────────

export interface CatalogItem {
  id: string
  name: string
  description: string
  price: number
  taxable: boolean
  active: boolean
  sortOrder: number
  priceType: string
  priceMax: number | null
}

export interface CatalogItemDraft {
  name: string
  description?: string
  price: number
  taxable: boolean
  priceType?: 'fixed' | 'hourly' | 'range' | 'quote_required'
  priceMax?: number
}

function mapCatalogItem(raw: any): CatalogItem {
  return {
    id: raw.id,
    name: raw.name,
    description: raw.description ?? '',
    price: Number(raw.price),
    taxable: !!raw.taxable,
    active: raw.active !== false,
    sortOrder: raw.sortOrder ?? 0,
    priceType: raw.priceType ?? 'fixed',
    priceMax: raw.priceMax != null ? Number(raw.priceMax) : null,
  }
}

export async function listCatalog(accountId: string): Promise<CatalogItem[]> {
  const raw = await api<any[]>(`/accounts/${accountId}/services`)
  return (raw ?? []).map(mapCatalogItem)
}

export async function addCatalogItem(
  accountId: string,
  item: CatalogItemDraft,
): Promise<CatalogItem> {
  const raw = await api<any>(`/accounts/${accountId}/services`, {
    method: 'POST',
    body: JSON.stringify(item),
  })
  return mapCatalogItem(raw)
}

export async function updateCatalogItem(
  accountId: string,
  itemId: string,
  updates: Partial<CatalogItemDraft>,
): Promise<CatalogItem> {
  const raw = await api<any>(`/accounts/${accountId}/services/${itemId}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  })
  return mapCatalogItem(raw)
}

export async function removeCatalogItem(
  accountId: string,
  itemId: string,
): Promise<void> {
  await api(`/accounts/${accountId}/services/${itemId}`, { method: 'DELETE' })
}

export interface CatalogCsvImportResult {
  added: number
  skipped: Array<{ row: number; name: string; reason: string }>
  errors: Array<{ row: number; reason: string }>
}

export async function importCatalogCsv(
  accountId: string,
  csv: string,
): Promise<CatalogCsvImportResult> {
  return api<CatalogCsvImportResult>(`/accounts/${accountId}/catalog/csv`, {
    method: 'POST',
    body: JSON.stringify({ csv }),
  })
}

export async function uploadPastInvoicePdf(
  accountId: string,
  file: File,
): Promise<{ url: string }> {
  const base64 = await fileToBase64(file)
  return api<{ url: string }>(`/accounts/${accountId}/catalog/invoices-upload`, {
    method: 'POST',
    body: JSON.stringify({
      data: base64,
      contentType: file.type || 'application/pdf',
      fileName: file.name,
    }),
  })
}

// ── Notification settings (full-transparency prefs) ────────────────────────
// GET returns the catalog + EFFECTIVE per-event values (per-account rows
// resolved over catalog defaults, locked channels forced on); PUT sends only
// the deltas and returns the fresh settings.

export interface NotificationSettings {
  catalog: NotificationCatalogEntry[]
  prefs: Record<string, { sms: boolean; email: boolean }>
  digest: { enabled: boolean; hourLocal: number }
  receiveSmsAlerts: boolean
}

export interface UpdateNotificationSettingsInput {
  prefs?: { key: string; sms?: boolean; email?: boolean }[]
  digestEnabled?: boolean
  digestHourLocal?: number
  receiveSmsAlerts?: boolean
}

export async function getNotificationSettings(accountId: string): Promise<NotificationSettings> {
  return api(`/accounts/${accountId}/notification-settings`)
}

export async function updateNotificationSettings(
  accountId: string,
  updates: UpdateNotificationSettingsInput,
): Promise<NotificationSettings> {
  return api(`/accounts/${accountId}/notification-settings`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  })
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result as string
      // strip "data:application/pdf;base64," prefix
      resolve(result.split(',')[1] ?? '')
    }
    reader.onerror = () => reject(new Error('Could not read file'))
    reader.readAsDataURL(file)
  })
}
